"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./QuestionSelector.css";
import StudentOverallInsightModal from "./StudentOverallInsightModal";
import { API_URL } from "../config";

const QuestionSelector = ({ assessmentId, onSelectQuestion, netId }) => {
  const [questions, setQuestions] = useState([]);
  const [completionStatus, setCompletionStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInsightModalOpen, setIsInsightModalOpen] = useState(false);
  const navigate = useNavigate();

  // Function to get the auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchQuestionsAndCompletion = async () => {
      try {
        setLoading(true);
        setError(null);

        // Encode the assessment ID for the URL
        const encodedAssessmentId = encodeURIComponent(assessmentId);

        // Build the questions URL with optional netId parameter
        let questionsUrl = `${API_URL}/api/assessment/${encodedAssessmentId}/questions`;
        if (netId) {
          questionsUrl += `?netId=${encodeURIComponent(netId)}`;
        }

        // Fetch questions
        const questionsResponse = await fetch(questionsUrl, {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "application/json",
          },
        });

        if (!questionsResponse.ok) {
          if (questionsResponse.status === 401) {
            // Handle unauthorized
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            navigate("/");
            return;
          }

          const errorData = await questionsResponse.json();
          throw new Error(errorData.detail || "Failed to fetch questions");
        }

        const questionsData = await questionsResponse.json();
        // Sort questions alphabetically by ID
        const sortedQuestions = questionsData.questions.sort((a, b) =>
          a.id.localeCompare(b.id, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
        setQuestions(sortedQuestions);

        // Fetch completion status if netId is available
        if (netId) {
          try {
            const completionResponse = await fetch(
              `${API_URL}/api/assessment/${encodedAssessmentId}/completion/${encodeURIComponent(
                netId
              )}`,
              {
                headers: {
                  ...getAuthHeader(),
                  "Content-Type": "application/json",
                },
              }
            );

            if (completionResponse.ok) {
              const completionData = await completionResponse.json();
              setCompletionStatus(completionData.completion_status || {});
            } else {
              // If completion fetch fails, just log it but don't break the flow
              console.warn(
                "Failed to fetch completion status:",
                completionResponse.status
              );
              setCompletionStatus({});
            }
          } catch (completionError) {
            console.warn("Error fetching completion status:", completionError);
            setCompletionStatus({});
          }
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
        setError(error.message);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    if (assessmentId) {
      fetchQuestionsAndCompletion();
    } else {
      setError("No assessment ID provided");
      setLoading(false);
    }
  }, [assessmentId, netId, navigate]);

  if (loading) return <div className="loading">Loading questions...</div>;
  if (error) return <div className="error">{error}</div>;
  if (questions.length === 0)
    return <div>No questions found for this assessment.</div>;

  return (
    <div className="question-selector">
      <h2>Select a Question</h2>
      <div className="questions-list">
        {questions.map((question) => {
          const isCompleted = completionStatus[question.id]?.completed;
          return (
            <div
              key={question.id}
              className={`question-item ${isCompleted ? "completed" : ""}`}
              onClick={() => onSelectQuestion(question)}
            >
              <div className="question-header">
                <h3>Question {question.id}</h3>
                {isCompleted && (
                  <div className="completion-indicator">
                    <span className="checkmark">✓</span>
                    <span className="completion-text">Completed</span>
                  </div>
                )}
              </div>
              {question.tables && question.tables.length > 0 && (
                <div className="question-tables">
                  <span className="tables-badge">
                    {question.tables.length} Table
                    {question.tables.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
              <div className="question-preview">
                {question.question.substring(0, 150)}
                {question.question.length > 150 ? "..." : ""}
              </div>
              {isCompleted && completionStatus[question.id]?.completed_at && (
                <div className="completion-details">
                  Completed on{" "}
                  {new Date(
                    completionStatus[question.id].completed_at
                  ).toLocaleDateString()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Button to open overall insight modal */}
      {netId && (
        <button
          className="open-insight-modal-button"
          onClick={() => setIsInsightModalOpen(true)}
        >
          View Overall Insight
        </button>
      )}

      {/* Overall Insight Modal */}
      <StudentOverallInsightModal
        assessmentId={assessmentId}
        netId={netId}
        isOpen={isInsightModalOpen}
        onClose={() => setIsInsightModalOpen(false)}
      />
    </div>
  );
};

export default QuestionSelector;
